// Module-access helpers — single source of truth for "which modules can
// this user touch". The auth store carries the booleans; this file
// resolves them into a uniform check the sidebar, route guards, and the
// ModuleLocked page can all share.
//
// Super Admin always passes — module flags are a per-tenant CLIENT_ADMIN
// concept. Other non-tenant roles (Proctor, Candidate) live in their own
// shells and don't see the client sidebar at all, so they aren't checked
// here.

import type { AuthUser, Role } from "@/stores/auth"

export type ModuleKey =
  | "assessments"
  | "multirater"
  | "qrQuiz"
  | "proctoring"
  | "aiAssistant"
  | "psychometrics"

export interface ModuleMeta {
  key: ModuleKey
  label: string
  description: string
  emoji: string
}

export const MODULES: Record<ModuleKey, ModuleMeta> = {
  assessments: {
    key: "assessments",
    label: "Assessments",
    description: "Create and manage assessments",
    emoji: "📝",
  },
  multirater: {
    key: "multirater",
    label: "Multirater",
    description: "90°/180°/360° feedback surveys",
    emoji: "👥",
  },
  qrQuiz: {
    key: "qrQuiz",
    label: "QR Quizzes",
    description: "Live QR scan quizzes and polls",
    emoji: "📱",
  },
  proctoring: {
    key: "proctoring",
    label: "Proctoring",
    description: "AI-powered exam proctoring",
    emoji: "🔍",
  },
  // No nav entry and no client route yet — the assistant's own UI is not built.
  // The metadata exists so the flag is togglable, readable and lockable through
  // exactly the same path as the other four the day that UI lands, rather than
  // being wired up separately and differently later.
  aiAssistant: {
    key: "aiAssistant",
    label: "AI Assistant",
    description: "Chat and voice assistant that can act on this account",
    emoji: "🤖",
  },
  // THE UI LANDED, and this comment used to say it had not: it claimed "no nav
  // entry and no client route yet", which stopped being true the day
  // /client/psychometrics shipped. Both exist now — a sidebar entry nested
  // under My Assessments, and two routes behind ModuleGuard — and this flag
  // gates all of them plus the "Use a platform instrument" option in the
  // create-assessment dialog.
  //
  // The metadata below is unchanged and still earns its place for the reason it
  // always did: the flag is togglable, readable and lockable through exactly
  // the same path as the other five.
  psychometrics: {
    key: "psychometrics",
    label: "Psychometrics",
    description: "Personality and psychometric profiling (Big Five/OCEAN)",
    emoji: "🧭",
  },
}

// Boolean field on AuthUser that corresponds to each module key.
const FIELD: Record<ModuleKey, keyof AuthUser> = {
  assessments: "moduleAssessments",
  multirater: "moduleMultirater",
  qrQuiz: "moduleQrQuiz",
  proctoring: "moduleProctoring",
  aiAssistant: "moduleAiAssistant",
  psychometrics: "modulePsychometrics",
}

export function hasModuleAccess(
  user: AuthUser | null,
  role: Role | null,
  module: ModuleKey,
): boolean {
  // Super Admin sees the whole platform (incl. when impersonating, they're
  // surfaced as CLIENT_ADMIN with the target client's actual flags loaded —
  // so the check below correctly mirrors what that client would see).
  if (role === "SUPER_ADMIN") return true
  if (!user) return false
  return Boolean(user[FIELD[module]])
}
